import gdnBgSources from "data/gdn-bg-sources.json";
import gdnBgThumbnails from "data/gdn-bg-thumbnails.json";
import {sortValuesByKey} from "./utils";
import {findBgUrl} from "./bg-utils";
import {bgMarkersFromUrl} from "./bgMarkers";
import type {BgUrls} from "hooks/useBgData";
import type {CatalogBackgroundLike} from "./bgCatalogIndex";

/**
 * Google Display Network backgrounds, copied from the Social channel to the v3
 * baker by `scripts/upload-gdn-bg-sources.mjs`. The manifest mirrors the shape
 * of the Kraken `/tiles/get_tilesets` response — `{ "1": [url, ...], ... }`,
 * one array per background variation — with the original Social filename kept
 * as a `#/<filename>` fragment on each v3 url. That fragment carries the
 * product code, logo/text-color markers, and `_&<hash>` transform hash the
 * frontend parses (findBgUrl / getBgMarkerClassNames / getHashes), so GDN can
 * reuse the exact Social resolution path with no Kraken/"gdrive" fetch.
 *
 * Precomputed once at module load (the JSON is static and bundled), sorted by
 * set number the same way `selectBackgrounds` sorts the live tileset response.
 */
const GDN_BG_TILESETS = sortValuesByKey(gdnBgSources as Record<string, string[]>) as BgUrls;
const GDN_BG_THUMBNAILS: Readonly<Record<string, string>> = gdnBgThumbnails;

// DataBao backgrounds 1–4 reuse Social artwork with a white canvas base. Keep
// this background property with the bundled GDN source instead of fetching the
// linked Social scene after the page is ready.
const GDN_BACKGROUND_BASES: Readonly<Record<string, Readonly<Record<number, string>>>> = {
  DBAO: {1: "fff", 2: "fff", 3: "fff", 4: "fff"},
};

/** GDN backgrounds in Kraken tile_sets shape (empty when the manifest is unpopulated). */
export function getGdnBgTilesets(): BgUrls {
  return GDN_BG_TILESETS;
}

/** Resolve a WebP icon by source URL. Filename markers do not change its pixels. */
export function getGdnBackgroundThumbnailUrl(imageUrl: string): string {
  const sourceUrl = imageUrl.split("#")[0];
  const thumbnailUrl = GDN_BG_THUMBNAILS[sourceUrl];
  if (!thumbnailUrl) {
    throw new Error(
      `Missing GDN WebP thumbnail for ${sourceUrl}. Run bun scripts/sync-gdn-bg-thumbnails.ts --write --verify.`
    );
  }
  return thumbnailUrl;
}

/** Bundled canvas base for a product and background variation. */
export function getGdnBackgroundBase(product: string, variation: string | number): string | null {
  const numericVariation = Number(variation);
  if (!Number.isInteger(numericVariation) || numericVariation < 1) return null;
  return GDN_BACKGROUND_BASES[product]?.[numericVariation] ?? null;
}

/**
 * Structured display-image rows for the v3 derivation path. GDN's bytes are
 * already v3 uploads; this projects the bundled upload manifest into the same
 * shape as GET /api/v3/backgrounds so URL resolution does not fall back to the
 * legacy filename cache while the ads/GDN catalog has no dedicated image rows.
 */
export function getGdnCatalogBackgrounds(product: string): CatalogBackgroundLike[] {
  return GDN_BG_TILESETS.flatMap((_, index) => {
    const variation = index + 1;
    const imageUrl = findBgUrl(GDN_BG_TILESETS, variation, `/${product}`);
    const outdatedUrl = findBgUrl(GDN_BG_TILESETS, variation, `/${product}-outdated`);
    if (!imageUrl && !outdatedUrl) return [];

    return [
      {
        variation,
        imageUrl: imageUrl ?? "",
        thumbnailUrl: imageUrl ? getGdnBackgroundThumbnailUrl(imageUrl) : "",
        miniUrl: null,
        outdatedUrl: outdatedUrl ?? null,
        markers: bgMarkersFromUrl(imageUrl ?? outdatedUrl),
      },
    ];
  });
}
